/**
 * RBAC permission access for the SPA. Reads the resolved `permissions` map off
 * the authenticated session (server-driven, resolved per request in
 * `/api/session`, propagated through `SessionContext`).
 *
 * UX only — the server `/api/*` gates are the source of truth. Public routes
 * (rendered outside `<AppShell>`) have no session in scope and must not call
 * these hooks; gate those flows server-side.
 *
 * Phase 2: these read the projected session state so the SPA can build against
 * them. Server-side enforcement lands in a later phase.
 */

import { useAuthSession, useOptionalAuthSession } from "@/lib/session-context";
import type { PermissionScope, SessionPermissions } from "@/types/permissions";

/** Full resolved permission state from the authenticated session. */
export function usePermissions(): SessionPermissions {
  return useAuthSession().permissions;
}

/** Whether the caller holds a permission. Admins hold everything. */
export function usePermission(key: string): boolean {
  const p = usePermissions();
  return p.is_admin || p.granted.includes(key);
}

/**
 * The caller's data-visibility scope for a scoped permission.
 * Admins → `"all"`. A granted scoped key → its stored scope. Anything else
 * (ungranted, or a binary permission with no scope) → `"none"`.
 */
export function useScope(key: string): PermissionScope {
  const p = usePermissions();
  if (p.is_admin) return "all";
  return (p.scopes[key] as PermissionScope | undefined) ?? "none";
}

/** Whether the caller's grant for a scoped permission also admits unassigned
 *  (NULL-owner) rows. */
export function useIncludesUnassigned(key: string): boolean {
  const p = usePermissions();
  if (p.is_admin) return true;
  return p.unassigned.includes(key);
}

/**
 * `usePermission` for components that can render with no session in scope —
 * the dashboard widgets reused on public pages, say. No session holds nothing.
 * The counterpart of `useOptionalFeature`; the same warning applies: do not
 * reach for it to paper over a missing provider in a test.
 */
export function useOptionalPermission(key: string): boolean {
  const p = useOptionalAuthSession()?.permissions;
  return p ? p.is_admin || p.granted.includes(key) : false;
}
